import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, UserPlus } from 'lucide-react';

import { useAuthStore } from '@/app/store/auth.store';
import { AdminEditPanel } from '@/features/admins/components/AdminEditPanel';
import { AdminInvitePanel } from '@/features/admins/components/AdminInvitePanel';
import { AdminListFilters } from '@/features/admins/components/AdminListFilters';
import { AdminListView } from '@/features/admins/components/AdminListView';
import { RootTransferPanel } from '@/features/admins/components/RootTransferPanel';
import { useRequirePermission } from '@/shared/hooks/useRequirePermission';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

// Set by My Profile (PRD 20 §"Transfer of Root Rights") before navigating here.
const AUTO_OPEN_KEY = 'autoOpenRootTransfer';

export function AdminManagementPage() {
  const { t } = useTranslation('admin');

  // PRD 15 §1.2: SYSTEM_MGMT:READ required to view; WRITE required to invite/edit/reset.
  // Root Transfer is independent of the permission matrix — only the current Root sees it.
  const canRead = useRequirePermission('SYSTEM_MGMT', 'READ');
  const { canWrite, denyProps, tooltip } = useWriteGuard('SYSTEM_MGMT');
  const isRoot = useAuthStore((s) => s.isRoot);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  // Auto-open hook: My Profile sets sessionStorage('autoOpenRootTransfer') = 'true'
  // before redirecting here. Fire once on mount when current user is Root.
  useEffect(() => {
    if (sessionStorage.getItem(AUTO_OPEN_KEY) === 'true') {
      sessionStorage.removeItem(AUTO_OPEN_KEY);
      if (isRoot) setTransferOpen(true);
    }
  }, [isRoot]);

  if (!canRead) return null;

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('adminManagement.title')}</h1>
        </div>
        <div className="content__actions">
          {/* SHCT-656 — the 📧 / 🔑 email-preview shortcuts are gone here too. Prototype
              scaffolding, not spec: PRD 15 §2.2 items 5-6 define this header as
              [관리자 초대] + [Root 이양] only. The admin invite panel keeps its own
              PRD-mandated preview (AdminInvitePanel → /admin-invite-email). */}
          {isRoot && (
            <button
              type="button"
              className="btn btn-default btn-outline"
              onClick={() => setTransferOpen(true)}
            >
              <KeyRound size={16} />
              {t('adminManagement.actions.rootTransfer')}
            </button>
          )}
          <button
            type="button"
            className="btn btn-default btn-primary"
            disabled={!canWrite}
            {...denyProps}
            title={!canWrite ? t('adminManagement.tooltips.noWritePermission') : undefined}
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus size={16} />
            {t('adminManagement.actions.inviteAdmin')}
          </button>
        </div>
      </div>
      <div className="content__body">
        <AdminListFilters />
        <AdminListView />
      </div>
      <AdminInvitePanel open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <AdminEditPanel />
      <RootTransferPanel open={transferOpen} onClose={() => setTransferOpen(false)} />
    </>
  );
}
